import { useNavigate } from '@tanstack/react-router';
import { useLayoutEffect, useRef, useState } from 'react';

import { useEventEmit } from '@common/utils/events';
import {
  getApiErrorInfo,
  isRateLimitedError,
  postEvent,
} from '@common/utils/fetcher';

import { disableFeatureOnError } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  findSuperlikeMessageRefusal,
  superlikeMessageErrorText,
} from '@entities/superlike/ui';

import { analytics } from '@services/AnalyticsService';
import { usePostSuperlike } from '@services/hooks/SuperlikesService';
import {
  type PostSuperlikeResponse,
  SUPERLIKE_ERROR_CODES,
} from '@services/SuperlikesService';
import { type User } from '@services/UsersService';

import { useSuperlikeQuota } from './useSuperlikeQuota';

/** After a held superlike's animation (the sender is not approved yet). */
export const SUPERLIKE_HELD_NOTICE = {
  title: 'Отправим, как только проверим анкету',
};
export const SUPERLIKE_UNAVAILABLE_NOTICE = { title: 'Анкета недоступна' };
export const SUPERLIKE_ALREADY_SENT_NOTICE = {
  title: 'Вы уже отправили суперлайк этой анкете',
};
export const SUPERLIKE_SEND_FAILED_NOTICE = {
  title: 'Не удалось отправить суперлайк',
  description: 'Попробуйте ещё раз',
};

export type UseSuperlikeSendParams = {
  /**
   * Checked before a sheet opens and before a send; true stops it (paused
   * swipes, an animation that is playing). The caller shows any notice.
   */
  isBlocked?: () => boolean;
  /**
   * 200: the sheet is closed and the counter written. Play the animation,
   * then remove the card like a like.
   */
  onSent: (profile: User, response: PostSuperlikeResponse) => void;
  /** 404 not_found, 409 already_superliked: the toast is shown; drop the card. */
  onDropped?: (profile: User) => void;
  /** 429 swipe_limit: the swipe limit's own flow. */
  onSwipeLimit?: (error: unknown) => void;
  /** 429 RATE_LIMITED: pause swipes. The sheet stays open with its text. */
  onRateLimited?: (error: unknown) => void;
  /** 403 not_dating. */
  onNotDating?: () => void;
};

/**
 * A superlike from the heart on a card to the server's answer (plan §2): the
 * message sheet, the limit sheet and every error, branched on
 * `errors.code`. No request opens the limit sheet when none are left. The
 * caller renders SuperlikeSheet and SuperlikeLimitSheet from what this
 * returns, and plays the animation in onSent.
 */
export const useSuperlikeSend = (params: UseSuperlikeSendParams) => {
  const navigate = useNavigate();
  const emit = useEventEmit();
  const isPremium = useAuthStore((state) => !!state.user?.is_premium);
  const { isEnabled, remaining, resetTime, writeQuota } = useSuperlikeQuota();
  const { trigger: triggerPostSuperlike, isMutating: isMutatingPostSuperlike } =
    usePostSuperlike();

  const [target, setTarget] = useState<User | null>(null);
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState<string>();
  const [limitSheet, setLimitSheet] = useState<{
    resetTime: string | null;
  } | null>(null);

  const paramsRef = useRef(params);
  const isSendingRef = useRef(false);
  // Whose message the field holds: closing the sheet keeps it for the same
  // card (a mis-tap on the dim after an error must not lose the text).
  const messageTargetIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    paramsRef.current = params;
  });

  const openLimitSheet = (limitResetTime: string | null) => {
    setLimitSheet({ resetTime: limitResetTime });
    analytics.trackSuperlikeLimitHit();
  };

  const open = (profile: User) => {
    if (paramsRef.current.isBlocked?.()) return;

    if (remaining === 0) {
      openLimitSheet(resetTime);

      return;
    }

    if (messageTargetIdRef.current !== profile.id) {
      messageTargetIdRef.current = profile.id;
      setMessage('');
    }

    setTarget(profile);
    setMessageError(undefined);
    analytics.trackSuperlikeOpen();
  };

  const close = () => {
    if (isSendingRef.current) return;

    setTarget(null);
    setMessageError(undefined);
  };

  const changeMessage = (text: string) => {
    setMessage(text);
    setMessageError(undefined);
  };

  const handleError = (profile: User, error: unknown) => {
    const handlers = paramsRef.current;

    if (isRateLimitedError(error)) {
      handlers.onRateLimited?.(error);

      return;
    }

    if (disableFeatureOnError(error, 'superlike')) {
      setTarget(null);

      return;
    }

    const { code, details } = getApiErrorInfo(error);

    switch (code) {
      case SUPERLIKE_ERROR_CODES.superlikeLimit: {
        const limitResetTime =
          typeof details?.reset_time === 'string' ? details.reset_time : null;

        writeQuota({ remaining: 0, reset_time: limitResetTime });
        setTarget(null);
        openLimitSheet(limitResetTime);

        return;
      }
      case SUPERLIKE_ERROR_CODES.swipeLimit:
        setTarget(null);
        handlers.onSwipeLimit?.(error);

        return;
      case SUPERLIKE_ERROR_CODES.notFound:
        setTarget(null);
        emit('error', SUPERLIKE_UNAVAILABLE_NOTICE);
        handlers.onDropped?.(profile);

        return;
      case SUPERLIKE_ERROR_CODES.alreadySuperliked:
        setTarget(null);
        emit('info', SUPERLIKE_ALREADY_SENT_NOTICE);
        handlers.onDropped?.(profile);

        return;
      case SUPERLIKE_ERROR_CODES.notDating:
        setTarget(null);
        handlers.onNotDating?.();

        return;
      case SUPERLIKE_ERROR_CODES.invalidMessage:
        setMessageError(superlikeMessageErrorText(details?.reason));

        return;
      default:
        emit('error', SUPERLIKE_SEND_FAILED_NOTICE);
    }
  };

  const send = async () => {
    const profile = target;

    if (!profile || isSendingRef.current) return;
    if (paramsRef.current.isBlocked?.()) return;

    const text = message.trim();
    const refusal = text ? findSuperlikeMessageRefusal(text) : null;

    if (refusal) {
      setMessageError(superlikeMessageErrorText(refusal));

      return;
    }

    isSendingRef.current = true;

    try {
      const response = await triggerPostSuperlike({
        target_user_id: profile.id,
        ...(text ? { message: text } : {}),
      });

      writeQuota(response);
      setTarget(null);
      setMessage('');
      messageTargetIdRef.current = null;
      analytics.trackSuperlikeSent(response.status, text.length > 0);
      paramsRef.current.onSent(profile, response);
    } catch (error) {
      handleError(profile, error);
    } finally {
      isSendingRef.current = false;
    }
  };

  const closeLimitSheet = () => setLimitSheet(null);

  const openPremium = () => {
    postEvent('paywall_cycle_hit', { source: 'superlike_limit' });
    setLimitSheet(null);
    void navigate({ to: '/premium' });
  };

  return {
    isEnabled,
    isPremium,
    remaining,
    target,
    message,
    messageError,
    isSending: isMutatingPostSuperlike,
    limitSheet,
    /** A sheet is open: the tab bar's slot stays empty meanwhile. */
    isSheetOpen: target !== null || limitSheet !== null,
    open,
    close,
    changeMessage,
    send,
    closeLimitSheet,
    openPremium,
  };
};

export type SuperlikeSendController = ReturnType<typeof useSuperlikeSend>;
